/**
 * UranIA PA — Login route (US-010).
 *
 * Tre opzioni di accesso allineate al CAD (art. 64) e alle Linee Guida AgID:
 *   - SPID smart-button con dropdown dei 9 IdP accreditati (AC#2).
 *   - CIE — Carta d'Identità Elettronica (US-005).
 *   - CNS — Carta Nazionale dei Servizi (US-006, riservata ad Auditor).
 *
 * Click su un IdP SPID o sul bottone CIE/CNS avvia OIDC code+PKCE contro
 * Keycloak `urania-pa-staging` (US-004/005/006); il redirect post-success
 * è `/dashboard` (AC#4) — gestito da `routes/auth/callback.tsx` che chiama
 * `completeFederatedLogin()` e fa il `router.navigate({to: '/dashboard'})`.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute } from '@tanstack/react-router';
import type { JSX } from 'react';

import { AltIdentityButton } from '../components/auth/AltIdentityButton.tsx';
import { SpidButton } from '../components/auth/SpidButton.tsx';
import { ALT_IDENTITY_OPTIONS, type SpidIdpOption } from '../lib/auth/idp-options.ts';
import { startFederatedLogin } from '../lib/auth/login-actions.ts';
import { Route as RootRoute } from './__root.tsx';

function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = useCallback(
    async (keycloakAlias: string, acrValues: string): Promise<void> => {
      setError(null);
      setRedirecting(true);
      try {
        await startFederatedLogin({ keycloakAlias, acrValues });
      } catch (err) {
        setRedirecting(false);
        setError(err instanceof Error ? err.message : t('common.error'));
      }
    },
    [t],
  );

  const handleSpid = useCallback(
    (option: SpidIdpOption): void => {
      void handleStart(option.keycloakAlias, 'L2');
    },
    [handleStart],
  );

  const handleCie = useCallback((): void => {
    void handleStart(ALT_IDENTITY_OPTIONS.cie.keycloakAlias, ALT_IDENTITY_OPTIONS.cie.acrValues);
  }, [handleStart]);

  const handleCns = useCallback((): void => {
    void handleStart(ALT_IDENTITY_OPTIONS.cns.keycloakAlias, ALT_IDENTITY_OPTIONS.cns.acrValues);
  }, [handleStart]);

  return (
    <section
      className="urania-login-page"
      data-testid="login-page"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div className="row g-0" style={{ minHeight: 'calc(100vh - 120px)' }}>
        {/* COLONNA SINISTRA — Hero informativo */}
        <div
          className="col-12 col-lg-7 d-flex align-items-center"
          style={{
            background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
            color: '#fff',
          }}
        >
          <div className="container py-5 px-4 px-md-5">
            <div className="text-center" style={{ maxWidth: 640, margin: '0 auto' }}>
              <span
                className="badge mb-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                }}
              >
                REGIONE SICILIANA · AUTORITÀ DI GESTIONE FESR/FSE+
              </span>
              <h1 className="display-4 fw-bold mb-3" style={{ lineHeight: 1.1 }}>
                UranIA PA
              </h1>
              <p className="lead mb-4" style={{ opacity: 0.92 }}>
                L'assistente AI legale per la programmazione FESR/FSE+ — Pilota PR FESR 2021-2027.
              </p>
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6">
                  <div
                    className="p-3 rounded h-100 text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-1">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M4 0h5.5L14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zM3 9a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 9zm0 2.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z" />
                      </svg>
                      <strong className="small">Drafting Mod5</strong>
                    </div>
                    <p className="small mb-0" style={{ opacity: 0.85 }}>
                      Base Giuridica, Avvisi, Decreti, Checklist con editor OnlyOffice e generazione AI.
                    </p>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div
                    className="p-3 rounded h-100 text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-1">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="m8 0 1.669.864 1.858.282.842 1.68 1.337 1.32L13.4 6l.306 1.854-1.337 1.32-.842 1.68-1.858.282L8 12l-1.669-.864-1.858-.282-.842-1.68-1.337-1.32L2.6 6l-.306-1.854 1.337-1.32.842-1.68L6.331.864 8 0z" />
                      </svg>
                      <strong className="small">Verifica GBER</strong>
                    </div>
                    <p className="small mb-0" style={{ opacity: 0.85 }}>
                      Compliance Reg. UE 651/2014 per gli aiuti di Stato.
                    </p>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div
                    className="p-3 rounded h-100 text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-1">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M1 2.828c.885-.37 2.154-.769 3.388-.893 1.33-.134 2.458.063 3.112.752v9.746c-.935-.53-2.12-.603-3.213-.493-1.18.12-2.37.461-3.287.811V2.828zm7.5-.141c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492V2.687z" />
                      </svg>
                      <strong className="small">RAG Manuale v5.0</strong>
                    </div>
                    <p className="small mb-0" style={{ opacity: 0.85 }}>
                      Risposte con citazioni [FONTE: …] obbligatorie (AgID P.7).
                    </p>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div
                    className="p-3 rounded h-100 text-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                  >
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-1">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M5.338 1.59a61 61 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56z" />
                      </svg>
                      <strong className="small">AgID + AI Act</strong>
                    </div>
                    <p className="small mb-0" style={{ opacity: 0.85 }}>
                      Audit WORM hash-chained, banner art. 50, dati su territorio UE.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* COLONNA DESTRA — Box di login */}
        <div
          className="col-12 col-lg-5 d-flex align-items-center"
          style={{ backgroundColor: '#fff' }}
        >
          <div className="w-100 px-4 px-md-5 py-5">
            <div style={{ maxWidth: 440, margin: '0 auto' }}>
              <h2 className="h4 mb-2">{t('login.page_title')}</h2>
              <p className="text-muted mb-4">{t('login.page_intro')}</p>

              {error !== null && (
                <div
                  className="alert alert-danger mb-3"
                  role="alert"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <div
                className="urania-login-options d-flex flex-column gap-3"
                data-testid="login-options"
              >
                <div className="urania-login-option" data-testid="login-option-spid">
                  <p className="text-muted small mb-2">{t('login.spid_intro')}</p>
                  <SpidButton onSelectIdp={handleSpid} disabled={redirecting} />
                </div>

                <div className="position-relative my-2 text-center">
                  <hr className="m-0" />
                  <span
                    className="position-absolute top-50 start-50 translate-middle px-3 small text-muted"
                    style={{ backgroundColor: '#fff' }}
                  >
                    {t('login.divider_or')}
                  </span>
                </div>

                <div className="urania-login-option" data-testid="login-option-cie">
                  <AltIdentityButton flavour="cie" onClick={handleCie} disabled={redirecting} />
                </div>
                <div className="urania-login-option" data-testid="login-option-cns">
                  <AltIdentityButton flavour="cns" onClick={handleCns} disabled={redirecting} />
                </div>
              </div>

              {/* Livelli ACR accettati */}
              <div className="mt-4 pt-3 border-top">
                <p className="small text-muted mb-2 fw-semibold">
                  {t('login.acr_heading')}
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <span
                    className="badge"
                    style={{
                      backgroundColor: '#e7f1ff',
                      color: '#084298',
                      border: '1px solid #b6d4fe',
                      fontSize: '0.7rem',
                    }}
                  >
                    SPID L2 · Funzionario AdG
                  </span>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: '#e7f1ff',
                      color: '#084298',
                      border: '1px solid #b6d4fe',
                      fontSize: '0.7rem',
                    }}
                  >
                    SPID L3 · Admin
                  </span>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: '#e7f1ff',
                      color: '#084298',
                      border: '1px solid #b6d4fe',
                      fontSize: '0.7rem',
                    }}
                  >
                    CIE · CNS
                  </span>
                </div>
              </div>

              {/* Help */}
              <section
                className="urania-login-help mt-4 pt-3 border-top"
                aria-labelledby="urania-login-help-heading"
              >
                <h3 id="urania-login-help-heading" className="h6 mb-2">
                  {t('login.help_heading')}
                </h3>
                <p className="small text-muted mb-1">{t('login.help_no_spid')}</p>
                <p className="small text-muted mb-0">{t('login.help_support')}</p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/login',
  component: LoginPage,
});
